import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  forwardRef,
  Input,
  Output,
  ViewChild
} from '@angular/core';
import { AbstractControl, NG_VALIDATORS, NG_VALUE_ACCESSOR, ValidationErrors, Validator } from '@angular/forms';
import { CustomControlValueAccessor } from '../../utils/custom-control-value-accessor/custom-control-value-accessor';

export interface ITokenFieldErrorMessages {
  duplicate?: string;
  invalidNumber?: string;
}

// id: number quando vem do backend; string (uuid) quando criado localmente.
export interface ITokenItem {
  id: string | number;
  label: string;
  text?: string;
}

export interface ITokenFieldSettings {
  placeholder?: string;
  isDisabled?: boolean;
  isReadOnly?: boolean;
  minLength?: number;
  maxLength?: number;
}

export type TokenFieldMode = 'text' | 'number';

@Component({
  selector: 'sgt-tokenfield-input',
  templateUrl: './tokenfield.component.html',
  styleUrls: ['./tokenfield.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => TokenFieldInputComponent),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => TokenFieldInputComponent),
      multi: true,
    },
  ],
})
export class TokenFieldInputComponent
  extends CustomControlValueAccessor<ITokenItem[]>
  implements Validator {
  @Input() settings: ITokenFieldSettings = {};
  @Input() mode: TokenFieldMode = 'text';
  @Input() errorMessages: ITokenFieldErrorMessages = {};

  @Output() tokensChange = new EventEmitter<ITokenItem[]>();

  @ViewChild('tokenInput', { static: true })
  tokenInput!: ElementRef<HTMLInputElement>;

  inputValue = '';
  errorKey: keyof ITokenFieldErrorMessages | null = null;

  private focusedTokenIndex = -1;
  private readonly separator = /[,\n;]/;
  private readonly integerPattern = /^-?\d+$/;
  private readonly defaultErrorMessages: Required<ITokenFieldErrorMessages> = {
    duplicate: 'Valor já adicionado.',
    invalidNumber: 'Apenas inteiros são aceitos.',
  };
  private onValidatorChange: () => void = () => undefined;

  constructor(private cdr: ChangeDetectorRef) {
    super();
    this._value = [];
  }

  get errorMessage(): string | null {
    if (!this.errorKey) {
      return null;
    }
    return this.errorMessages[this.errorKey] ?? this.defaultErrorMessages[this.errorKey];
  }

  validate(_control: AbstractControl): ValidationErrors | null {
    return this.errorKey ? { [this.errorKey]: true } : null;
  }

  registerOnValidatorChange(fn: () => void): void {
    this.onValidatorChange = fn;
  }

  @Input() set tokens(value: ITokenItem[]) {
    this.updateValue(value);
  }

  get tokens(): ITokenItem[] {
    return this._value ?? [];
  }

  protected updateValue(value: ITokenItem[] | null): void {
    this._value = value || [];
    this.cdr.markForCheck();
  }

  protected onDisabledStateChange(isDisabled: boolean): void {
    this.settings = { ...this.settings, isDisabled };
    this.cdr.markForCheck();
  }

  onKeyDown(event: KeyboardEvent): void {
    const value = this.inputValue.trim();

    const keyActions: Record<string, () => void> = {
      Enter: () => this.createToken(value, event),
      Tab: () => this.createToken(value, event),
      Delete: () => this.focusedTokenDeletion(),
      Backspace: () => this.focusedTokenDeletionByBackspace(),
    };

    keyActions[event.key]?.();
  }

  private createToken(value: string, event: KeyboardEvent): void {
    event.preventDefault();
    if (value) {
      this.addToken(value);
    }
  }

  private focusedTokenDeletion(): void {
    if (this.focusedTokenIndex >= 0) {
      const tokenToRemove = this.tokens[this.focusedTokenIndex];
      this.removeToken(tokenToRemove);
      this.resetTokenFocus();
    }
  }

  private focusedTokenDeletionByBackspace(): void {
    if (!this.inputValue && this.tokens.length > 0) {
      const lastToken = this.tokens[this.tokens.length - 1];
      this.removeToken(lastToken);
    } else if (this.focusedTokenIndex >= 0) {
      const focusedToken = this.tokens[this.focusedTokenIndex];
      this.removeToken(focusedToken);
      this.resetTokenFocus();
    }
  }

  private resetTokenFocus(): void {
    this.focusedTokenIndex = -1;
  }

  onInput(event: Event): void {
    const target = event.target as HTMLInputElement;
    this.inputValue = target.value;

    if (this.errorKey && !this.currentValueReproducesError()) {
      this.clearError();
    }

    if (this.separator.test(this.inputValue)) {
      const parts = this.inputValue.split(this.separator);
      const tokensToAdd = parts.slice(0, -1).filter((part) => part.trim());

      tokensToAdd.forEach((token) => this.addToken(token.trim()));
      this.inputValue = parts[parts.length - 1];
      target.value = this.inputValue;
    }
  }

  onPaste(event: ClipboardEvent): void {
    event.preventDefault();
    const pastedText = event.clipboardData?.getData('text') || '';
    const tokens = pastedText.split(this.separator).filter(Boolean);

    tokens.forEach((token) => this.addToken(token.trim()));
  }

  private hasToken(label: string): boolean {
    return this.tokens.some((token) => token.label === label);
  }

  addToken(label: string): void {
    if (this.settings?.isDisabled || this.settings?.isReadOnly) {
      return;
    }

    if (!label || label.length < (this.settings?.minLength ?? 0)) {
      return;
    }

    if (this.mode === 'number' && !this.integerPattern.test(label)) {
      this.setError('invalidNumber');
      return;
    }
    if (this.hasToken(label)) {
      this.setError('duplicate');
      return;
    }

    const newToken: ITokenItem = {
      id: this.generateLocalId(),
      label,
    };

    const updatedTokens = [...this.tokens, newToken];
    this.resetInput();
    this.emitTokensChange(updatedTokens);
  }

  private resetInput(): void {
    this.inputValue = '';
    this.tokenInput.nativeElement.value = '';
    this.resetTokenFocus();
  }

  trackByTokenId(_index: number, token: ITokenItem): string | number {
    return token.id;
  }

  removeToken(token: ITokenItem): void {
    if (this.settings?.isDisabled || this.settings?.isReadOnly) {
return;
}

    const updatedTokens = this.tokens.filter((t) => t.id !== token.id);
    this.emitTokensChange(updatedTokens);
  }

  onBlur(): void {
    if (this.inputValue.trim()) {
      this.addToken(this.inputValue.trim());
    }
    this.resetTokenFocus();
    this.markAsTouched();
    this.cdr.markForCheck();
  }

  private emitTokensChange(tokens: ITokenItem[]): void {
    this._value = tokens;
    this.onChange(tokens);
    this.tokensChange.emit(tokens);
    this.clearError();
    this.cdr.markForCheck();
  }

  private setError(key: keyof ITokenFieldErrorMessages): void {
    if (this.errorKey === key) {
      return;
    }
    this.errorKey = key;
    this.onValidatorChange();
    this.cdr.markForCheck();
  }

  private clearError(): void {
    if (!this.errorKey) {
      return;
    }
    this.errorKey = null;
    this.onValidatorChange();
    this.cdr.markForCheck();
  }

  private currentValueReproducesError(): boolean {
    const value = this.inputValue.trim();
    if (!value) {
      return false;
    }
    if (this.errorKey === 'duplicate') {
      return this.hasToken(value);
    }
    if (this.errorKey === 'invalidNumber') {
      return this.mode === 'number' && !this.integerPattern.test(value);
    }
    return false;
  }

  private generateLocalId(): string {
    const cryptoRef = typeof crypto !== 'undefined' ? crypto : undefined;
    if (cryptoRef?.randomUUID) {
      return cryptoRef.randomUUID();
    }
    return `local-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
  }
}
